import { ALL_LNGS_NAMESPACES } from '@ingenico/util-i18n-config';
import { CoreStatus, StatusSize } from '@ingenico/react-ui/core-status';
import { CoreTypography, CoreTypographyVariant } from '@ingenico/react-ui/core-typography';
import { SideDrawer } from '@ingenico/react-ui/side-drawer';
import { LinearProgress } from '@mui/material';
import classNames from 'classnames';
import { toString } from 'lodash-es';
import type { ReactElement, ReactNode } from 'react';
import type { DeviceDiagnosticsDetailsDrawerProps } from '../../pages/device-diagnostic-page/DeviceDiagnosticDetailsPage.model';
import styles from './DeviceDiagnosticsDetailsDrawer.module.scss';
import { useDeviceDiagnosticDetailsDrawer } from './DeviceDiagnosticsDetailsDrawer.vm';

export function DeviceDiagnosticsDetailsDrawer({
  isOpen,
  onCloseDrawer,
  isLoading,
  testResultDetails,
}: DeviceDiagnosticsDetailsDrawerProps): ReactElement {
  const { resultCode, result, details, fix, translatedModule, t } = useDeviceDiagnosticDetailsDrawer(testResultDetails);

  return (
    <SideDrawer
      customDrawerHeaderWrapper={styles.customDrawerHeaderWrapper}
      subtitle={
        <div className={styles.drawerHeader}>
          <CoreStatus
            label={t(`statusTranslation.${testResultDetails?.status}`, {
              ns: ALL_LNGS_NAMESPACES.LOGICAL_DEVICE,
            })}
            status={testResultDetails?.status === 'PASSED' ? 'COMPLETED' : testResultDetails?.status}
            size={StatusSize.Small}
          />
        </div>
      }
      title={toString(translatedModule)}
      open={isOpen}
      onClose={onCloseDrawer}
    >
      {isLoading && <LinearProgress />}
      {/*RESULT*/}
      <div className={classNames(styles.targetDeviceDetailsContainer)}>
        <CoreTypography
          customClassName={classNames(styles.targetDeviceDetailsContainer__subTitleContainer__subTitle)}
          variant={CoreTypographyVariant.Body02Medium}
        >
          {t('details.result', { ns: ALL_LNGS_NAMESPACES.ERROR_DIAGNOSTICS })}
        </CoreTypography>
        <div className={classNames(styles.resultContainer)}>
          <CoreTypography
            customClassName={styles.targetDeviceDetailsContainer__subTitleContainer__descriptionCode}
            variant={CoreTypographyVariant.Body02Medium}
          >
            {resultCode}
          </CoreTypography>
          <CoreTypography
            customClassName={styles.targetDeviceDetailsContainer__subTitleContainer__description}
            variant={CoreTypographyVariant.Body02Regular}
          >
            {`- ${result}`}
          </CoreTypography>
        </div>
      </div>
      {/*DETAILS*/}
      {details && (
        <div className={classNames(styles.targetDeviceDetailsContainer)}>
          <CoreTypography
            customClassName={classNames(styles.targetDeviceDetailsContainer__subTitleContainer__subTitle)}
            variant={CoreTypographyVariant.Body02Medium}
          >
            {t('details.details', { ns: ALL_LNGS_NAMESPACES.ERROR_DIAGNOSTICS })}
          </CoreTypography>
          <CoreTypography
            customClassName={styles.targetDeviceDetailsContainer__subTitleContainer__details}
            variant={CoreTypographyVariant.Body02Regular}
          >
            {details}
          </CoreTypography>
        </div>
      )}
      {/*HOW TO FIX*/}
      {fix && (
        <div className={classNames(styles.targetDeviceDetailsContainer)}>
          <CoreTypography
            customClassName={classNames(styles.targetDeviceDetailsContainer__subTitleContainer__subTitle)}
            variant={CoreTypographyVariant.Body02Medium}
          >
            {t('details.fix', { ns: ALL_LNGS_NAMESPACES.ERROR_DIAGNOSTICS })}
          </CoreTypography>
          <div className={styles.targetDeviceDetailsContainer__subTitleContainer__fix}>{renderDiagnosticFix(fix)}</div>
        </div>
      )}
    </SideDrawer>
  );
}

function renderDiagnosticFix(fix: string): ReactNode {
  const listStart = fix.indexOf('<ul>');
  const listEnd = fix.indexOf('</ul>');
  if (listStart < 0 || listEnd < listStart) return fix.replaceAll(/<[^>]*>/g, '');

  const prefix = fix.slice(0, listStart).replaceAll(/<[^>]*>/g, '');
  const listContent = fix.slice(listStart + '<ul>'.length, listEnd);
  const suffix = fix.slice(listEnd + '</ul>'.length).replaceAll(/<[^>]*>/g, '');
  const items = [...listContent.matchAll(/<li>(.*?)<\/li>/g)].map((match) => match[1]);

  return (
    <>
      {prefix}
      <ul>
        {items.map((item, index) => (
          <li key={`${index}-${item}`}>{item}</li>
        ))}
      </ul>
      {suffix}
    </>
  );
}
